import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { UnlockDialog } from '@apps/guest/packages/layout-engine/components/UnlockDialog/UnlockDialog';
import { useDisclosure } from '@withjoy/joykit';
import { withWindow } from '@shared/utils/withWindow';
import { UnlockDialogContext } from './UnlockDialog.context';
import type { FindInviteSuccessBehavior, UnlockDialogOpenOptions } from './UnlockDialog.types';
import { useDialogThemeController } from '@apps/guest/packages/layout-engine/components/DialogStyled/DialogStyled.controller';

export const UnlockDialogProvider: React.FC = ({ children }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isThemeReady, pageColor, pageFontColor } = useDialogThemeController();
  const [shouldDisplayFindInvite, setShouldDisplayFindInvite] = useState<boolean>(false);
  const [shouldSkipPasswordGate, setShouldSkipPasswordGate] = useState<boolean>(false);
  const [findInviteSuccessBehavior, setFindInviteSuccessBehavior] = useState<FindInviteSuccessBehavior>('reloadInPlace');
  const [isSpecificGuestsUnlock, setIsSpecificGuestsUnlock] = useState<boolean>(false);
  // Ref, not state: simple-flow auto-close calls handleClose synchronously
  // after markUnlocked, so we need the latest value without waiting for a re-render.
  const didUnlockRef = useRef<boolean>(false);
  // Holds a pending open intent when the theme query hasn't resolved yet.
  const pendingOpenRef = useRef<boolean>(false);

  const markUnlocked = useCallback(() => {
    didUnlockRef.current = true;
  }, []);

  const handleUnlockDialogOpen = useCallback(
    (options?: UnlockDialogOpenOptions) => {
      const { displayFindInvite = false, skipPasswordGate = false, successBehavior = 'reloadInPlace', isSpecificGuestsUnlock: specificGuestsUnlock = false } = options ?? {};
      setShouldDisplayFindInvite(displayFindInvite);
      setShouldSkipPasswordGate(skipPasswordGate);
      setFindInviteSuccessBehavior(successBehavior);
      setIsSpecificGuestsUnlock(specificGuestsUnlock);
      didUnlockRef.current = false;
      if (isThemeReady) {
        onOpen();
      } else {
        pendingOpenRef.current = true;
      }
    },
    [isThemeReady, onOpen],
  );

  useEffect(() => {
    if (isThemeReady && pendingOpenRef.current) {
      pendingOpenRef.current = false;
      onOpen();
    }
  }, [isThemeReady, onOpen]);

  const handleClose = useCallback(() => {
    // Drop any open intent still waiting on the theme query. Without this, closing before the theme
    // resolves leaves the flag set and the effect above opens the dialog once it does — in front of
    // a guest who has already dealt with it.
    pendingOpenRef.current = false;
    if (didUnlockRef.current) {
      // Full page reload — every query refetches with the unlocked auth context.
      // Simpler and more durable than tracking which Apollo queries to invalidate.
      withWindow(global => global.location.reload());
    }
    // Always run onClose: in production the reload discards the heap before this
    // commits; in JSDOM (reload no-ops) this keeps React state consistent so
    // existing spec assertions of "dialog closes after unlock" still hold.
    onClose();
  }, [onClose]);

  // Memoised so consumers (every schedule list and event menu on the guest site) re-render only
  // when the dialog state changes, not whenever this provider or its parent renders.
  const contextValue = useMemo(
    () => ({ isOpen, handleUnlockDialogOpen, onClose: handleClose, shouldDisplayFindInvite, markUnlocked }),
    [isOpen, handleUnlockDialogOpen, handleClose, shouldDisplayFindInvite, markUnlocked],
  );

  return (
    <UnlockDialogContext.Provider value={contextValue}>
      {children}
      <UnlockDialog
        isOpen={isOpen}
        onClose={handleClose}
        shouldDisplayFindInvite={shouldDisplayFindInvite}
        shouldSkipPasswordGate={shouldSkipPasswordGate}
        findInviteSuccessBehavior={findInviteSuccessBehavior}
        isSpecificGuestsUnlock={isSpecificGuestsUnlock}
        pageColor={pageColor}
        pageFontColor={pageFontColor}
      />
    </UnlockDialogContext.Provider>
  );
};

export { UnlockDialogContext, useUnlockDialogContext } from './UnlockDialog.context';
export type { UnlockDialogContextValue } from './UnlockDialog.context';
